import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Anchor, Box, Center, Flex, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import { Panel } from '../../components/global/Panel/Panel.tsx';
import { CenterLayout } from '../../components/layout/CenterLayout.tsx';
import { Button } from '../../components/ui/Button/Button.tsx';
import { SocialLoginButton } from '../../components/ui/SocialLoginButton/SocialLoginButton.tsx';
import { env } from '../../env.ts';
import { Login as LoginFormContent } from '../../forms/auth/Login/Login.tsx';
import { schema } from '../../forms/auth/Login/schema.ts';
import type { FormLogin } from '../../forms/auth/Login/schema.ts';
import { useLogin } from '../../hooks/data/auth.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import type { DirectusError } from '../../types/directusError.ts';

export const Login: FC = () => {
  const { content } = useI18n();
  const navigate = useNavigate();

  const [globalError, setGlobalError] = useState<string | undefined>(undefined);

  const { mutate, isPending } = useLogin<DirectusError>({
    onSuccess: () => {
      void navigate('/dashboard');
    },
    onError: (error) => {
      const parsedErrors = error.errors.map((errorItem) => {
        if (errorItem.extensions.code === 'INVALID_CREDENTIALS') {
          // Not a field of content_login_translations (yet), so this falls back to the English text.
          const invalidCredentials = (content?.login as unknown as Record<string, unknown> | undefined)
            ?.error_invalid_credentials;
          return typeof invalidCredentials === 'string' ? invalidCredentials : 'Invalid credentials';
        }

        return errorItem.message;
      });

      setGlobalError(parsedErrors.join(', '));
    },
  });

  const formMethods = useForm<FormLogin>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (!content?.login) {
    return null;
  }

  const {
    title,
    facebook_button_label,
    google_button_label,
    inbetween_text,
    go_to_signup_description,
    go_to_signup_text,
    go_to_forgot_password_text,
    image,
    cta_button,
  } = content.login;

  return (
    <CenterLayout imageSrc={image ? `${env.VITE_DIRECTUS_API_URL}/assets/${image}` : undefined}>
      <Panel>
        <Title order={1}>{title}</Title>
        <Stack
          component="form"
          gap="sm"
          onSubmit={(event) => void formMethods.handleSubmit((data) => mutate(data))(event)}
        >
          <LoginFormContent forgotPasswordText={go_to_forgot_password_text} useFormValue={formMethods} />
          <Button loading={isPending} text={cta_button ?? 'Login'} type="submit" />
        </Stack>
        {!!globalError && (
          <Alert color="gigi-apple-pink" p="xs" radius="xs">
            {globalError}
          </Alert>
        )}
        <Flex align="center" gap="xxs">
          <Box bg="gigi-outlines" flex="1 0 auto" h={2} />
          <Text size="sm" ta="center">
            {inbetween_text}
          </Text>
          <Box bg="gigi-outlines" flex="1 0 auto" h={2} />
        </Flex>
        <Stack gap="xxs">
          <SocialLoginButton text={facebook_button_label ?? 'Login with facebook'} type="facebook" />
          <SocialLoginButton text={google_button_label ?? 'Login with google'} type="google" />
        </Stack>
        <Center>
          <Text c="gigi-grey-text" mt="xl" size="xs" ta="center">
            {go_to_signup_description}{' '}
            <Anchor component={Link} to="/auth/signup">
              {go_to_signup_text}
            </Anchor>
          </Text>
        </Center>
      </Panel>
    </CenterLayout>
  );
};
